import { useEffect, useMemo, useState } from 'react'
import { HeartbeatIcon } from '../components/icons/HeartbeatIcon'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useApiFetch } from '../lib/api'
import { useDirty } from '../hooks/useDirty'
import { usePageNavigationGuard } from '../hooks/useUnsavedChangesGuard'
import { hasDirtyOpenEntityEditor, markAllEntityEditorsClean } from '../lib/entityEditorRegistry'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { UnsavedChangesDialog } from '../components/UnsavedChangesDialog'
import { PageSaveDiscardActions } from '../components/PageSaveDiscardActions'
import { SuccessToast } from '../components/SuccessToast'
import { Spinner } from '../components/Spinner'
import { PageHeader } from '../components/PageHeader'
import { TenantTab } from './mambuMcp/TenantTab'
import { TemplatesTab } from './mambuMcp/TemplatesTab'
import { UsersTab } from './mambuMcp/UsersTab'
import type {
  MambuConnectionCatalogResponse,
  MambuConnectionDetail,
  MambuConnectionUpdate,
  MambuConnectionOperationToggle,
  PermissionTemplateListResponse,
  UserAccessListResponse,
} from '../lib/types'

/* ─── Form state ─── */

type TabId = 'tenant' | 'templates' | 'users'
const TAB_IDS: TabId[] = ['tenant', 'templates', 'users']
const DEFAULT_TAB: TabId = 'tenant'

function parseTab(raw: string | null): TabId {
  return TAB_IDS.includes(raw as TabId) ? (raw as TabId) : DEFAULT_TAB
}

interface TenantFormState {
  isEnabled: boolean
  defaultPermissionTemplateId: string | null
  operations: MambuConnectionOperationToggle[]
}

function initForm(d: MambuConnectionDetail): TenantFormState {
  return {
    isEnabled: d.isEnabled,
    defaultPermissionTemplateId: d.defaultPermissionTemplateId,
    operations: d.operations.map(op => ({ operationId: op.id, isEnabled: op.isEnabled })),
  }
}

function sortedOps(ops: MambuConnectionOperationToggle[]): MambuConnectionOperationToggle[] {
  return [...ops].sort((a, b) => a.operationId.localeCompare(b.operationId))
}

function isFormDirty(form: TenantFormState, saved: TenantFormState): boolean {
  const f = { ...form, operations: sortedOps(form.operations) }
  const s = { ...saved, operations: sortedOps(saved.operations) }
  return JSON.stringify(f) !== JSON.stringify(s)
}

/* ─── Main component ─── */

export function MambuMcpDetail() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const apiFetch = useApiFetch()
  const queryClient = useQueryClient()

  /* ── Active tab lives in the URL (`/mcp/:product/:tab`), so a reload or a shared
     link lands on the tab the admin was actually on. Derived state, never mirrored
     into useState, and written in exactly ONE place (`selectTab`) — MS-693 AC13
     calls out the prototype defect of two handlers writing the location for a
     single click, the second overwriting with a form that then fails to match on
     reload. ── */
  const { product, tab: tabParam } = useParams<{ product: string; tab: string }>()
  const activeTab = parseTab(tabParam ?? null)
  useEffect(() => {
    if (tabParam && !TAB_IDS.includes(tabParam as TabId)) {
      navigate(`/mcp/${product}/${DEFAULT_TAB}`, { replace: true })
    }
  }, [tabParam, product, navigate])
  function commitTab(next: TabId) {
    navigate(`/mcp/${product}/${next}`)
  }
  function selectTab(next: TabId) {
    if (next === activeTab) return
    // Entity scope (§7a): the template and Individual User Access editors commit their own
    // entity, so a tab switch would unmount them and lose the edit without this check.
    if (hasDirtyOpenEntityEditor()) {
      setPendingTab(next)
      setEntityTabSwitchConfirm(true)
      return
    }
    if (dirty) {
      setPendingTab(next)
      setTabSwitchDialog(true)
      return
    }
    commitTab(next)
  }

  const [form, setForm] = useState<TenantFormState | null>(null)
  const [savedForm, setSavedForm] = useState<TenantFormState | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [defaultAccessConfirm, setDefaultAccessConfirm] = useState(false)
  const [pendingTab, setPendingTab] = useState<TabId | null>(null)
  const [entityTabSwitchConfirm, setEntityTabSwitchConfirm] = useState(false)
  const [tabSwitchDialog, setTabSwitchDialog] = useState(false)
  const [tabToSwitchAfterSave, setTabToSwitchAfterSave] = useState<TabId | null>(null)
  const [toast, setToast] = useState<string | null>(null)

  useEffect(() => {
    if (!toast) return
    const id = setTimeout(() => setToast(null), 2500)
    return () => clearTimeout(id)
  }, [toast])

  const TAB_LABEL_MAP: Record<TabId, string> = useMemo(() => ({
    tenant: t('mambuMcpDetail.tabs.tenant'),
    templates: t('mambuMcpDetail.tabs.templates'),
    users: t('mambuMcpDetail.tabs.users'),
  }), [t])

  /* ─── Resolve the live connection id from the catalog ─── */
  const catalogQuery = useQuery({
    queryKey: ['mambu-connections', 'catalog'],
    queryFn: () => apiFetch<MambuConnectionCatalogResponse>('/api/v1/mambu-connections'),
  })
  const connectionId = catalogQuery.data?.items.find(i => i.status === 'live')?.id ?? null

  const detailQuery = useQuery({
    queryKey: ['mambu-connections', connectionId],
    queryFn: () => apiFetch<MambuConnectionDetail>(`/api/v1/mambu-connections/${connectionId}`),
    enabled: !!connectionId,
  })

  const templatesQuery = useQuery({
    queryKey: ['mambu-connections', connectionId, 'templates'],
    queryFn: () => apiFetch<PermissionTemplateListResponse>(`/api/v1/mambu-connections/${connectionId}/permission-templates`),
    enabled: !!connectionId,
  })

  const userAccessQuery = useQuery({
    queryKey: ['mambu-connections', connectionId, 'user-access'],
    queryFn: () => apiFetch<UserAccessListResponse>(`/api/v1/mambu-connections/${connectionId}/user-access`),
    enabled: !!connectionId,
  })

  /* ─── Initialise form when detail loads ─── */
  useMemo(() => {
    if (detailQuery.data && !form) {
      const f = initForm(detailQuery.data)
      setForm(f)
      setSavedForm(f)
    }
  }, [detailQuery.data, form])

  /* ─── Save mutation ─── */
  const saveMutation = useMutation({
    mutationFn: (payload: MambuConnectionUpdate) =>
      apiFetch<MambuConnectionDetail>(`/api/v1/mambu-connections/${connectionId}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      }),
    onSuccess: (data) => {
      const f = initForm(data)
      setForm(f)
      setSavedForm(f)
      setSaveError(null)
      setDefaultAccessConfirm(false)
      setToast(t('mambuMcpDetail.savedToast'))
      queryClient.setQueryData(['mambu-connections', connectionId], data)
      // The MCP list card reads its own key — refresh it so a save is visible
      // there immediately rather than after the app-wide `staleTime` window.
      queryClient.invalidateQueries({ queryKey: ['mambu-connections', 'catalog'] })
      if (tabToSwitchAfterSave) {
        commitTab(tabToSwitchAfterSave)
        setTabToSwitchAfterSave(null)
      }
    },
    onError: (err: unknown) => {
      setSaveError(err instanceof Error ? err.message : t('mambuMcpDetail.errors.failedToSave'))
      setDefaultAccessConfirm(false)
    },
  })

  const dirty = form && savedForm ? isFormDirty(form, savedForm) : false

  const { setDirty: publishDirty, clearDirty } = useDirty('mambu-mcp-detail')
  useEffect(() => { publishDirty(dirty) }, [dirty, publishDirty])

  /* ── In-app navigation guard (left-nav, breadcrumb, brand click) — consolidated
     hook. Also consults the entity-editor registry, which the template and
     Individual User Access editors register into (§7a), so entity scope is
     covered too. ── */
  const { dialogOpen: navBlockDialog, confirmLeave: confirmNavLeave, cancelLeave: cancelNavLeave } = usePageNavigationGuard(dirty, clearDirty)

  function patchForm(patch: Partial<TenantFormState>) {
    setForm(f => f ? { ...f, ...patch } : f)
  }

  function handleOperationToggle(operationId: string, isEnabled: boolean) {
    setForm(f => {
      if (!f) return f
      const exists = f.operations.some(o => o.operationId === operationId)
      const operations = exists
        ? f.operations.map(o => o.operationId === operationId ? { ...o, isEnabled } : o)
        : [...f.operations, { operationId, isEnabled }]
      return { ...f, operations }
    })
  }

  function handleDefaultPermissionTemplateIdChange(next: string | null) {
    patchForm({ defaultPermissionTemplateId: next })
  }

  function commitDiscard() {
    if (savedForm) setForm({ ...savedForm, operations: savedForm.operations.map(o => ({ ...o })) })
    setSaveError(null)
  }

  function commitSave() {
    if (!form) return
    saveMutation.mutate({
      isEnabled: form.isEnabled,
      defaultPermissionTemplateId: form.defaultPermissionTemplateId,
      operations: form.operations,
    })
  }

  function handleSaveClick() {
    if (!form || !savedForm) return
    setSaveError(null)
    const defaultAccessChanged = form.defaultPermissionTemplateId !== savedForm.defaultPermissionTemplateId
    if (defaultAccessChanged) {
      setDefaultAccessConfirm(true)
      return
    }
    commitSave()
  }

  /* ─── Loading / error states ─── */

  const isLoading = catalogQuery.isLoading || (!!connectionId && (detailQuery.isLoading || templatesQuery.isLoading || userAccessQuery.isLoading))
  const isError = catalogQuery.isError || detailQuery.isError || templatesQuery.isError || userAccessQuery.isError
  const notFound = !catalogQuery.isLoading && !catalogQuery.isError && !connectionId

  if (isLoading) {
    return (
      <div className="page-body" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Spinner />
      </div>
    )
  }

  if (isError || notFound) {
    return (
      <div className="page-body">
        <div role="alert" style={{ background: 'var(--danger-bg)', border: '1px solid var(--danger)', borderRadius: 8, padding: '12px 16px', fontSize: 13, color: 'var(--danger)' }}>
          {t('mambuMcpDetail.errors.failedToLoad')}
        </div>
      </div>
    )
  }

  if (!form || !savedForm || !detailQuery.data) return null

  const detail = detailQuery.data
  const templates = templatesQuery.data?.items ?? []
  const userAccess = userAccessQuery.data?.items ?? []

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
      {/* ── .sparks-main wrapper — contains header + tabs + scrollable content ── */}
      <div className="sparks-main">
        {/* ── Header ── */}
        <PageHeader
          variant="detail"
          icon={<div className="agent-grid-icon" aria-hidden="true" style={{ color: 'var(--accent-text)' }}><HeartbeatIcon size={22} /></div>}
          title={detail.name}
          /* Surface copy, not detail.description: the prototype's Core config header
             reads "Live access to core banking data and operations"
             (agent-studio.html:5579), while the stored description is what the
             prototype shows on the Agent → Connections tab (:4047). The prototype
             always renders this line, so there is no guard. */
          caption={t('mambuMcp.core.description')}
          actions={
            <PageSaveDiscardActions
              dirty={dirty}
              saving={saveMutation.isPending}
              onSave={handleSaveClick}
              onDiscard={commitDiscard}
              saveLabel={t('mambuMcpDetail.save')}
              discardLabel={t('mambuMcpDetail.discard')}
            />
          }
        />

        {saveError && (
          <div role="alert" style={{ marginTop: 12, background: 'var(--danger-bg)', border: '1px solid var(--danger)', borderRadius: 8, padding: '10px 16px', fontSize: 13, color: 'var(--danger)' }}>
            {saveError}
          </div>
        )}

        {/* ── Tabs ── */}
        <div role="tablist" aria-label={t('mambuMcpDetail.tabs.tenant')} className="tabs">
          {TAB_IDS.map(tab => (
            <button
              key={tab}
              type="button"
              role="tab"
              id={`mmcp-tab-${tab}`}
              aria-selected={activeTab === tab}
              aria-controls={`mmcp-tabpanel-${tab}`}
              onClick={() => selectTab(tab)}
              className={`tab-btn${activeTab === tab ? ' active' : ''}`}
            >
              {TAB_LABEL_MAP[tab]}
            </button>
          ))}
        </div>

        {/* ── Scrollable tab content ── */}
        <div className="tab-scroll-area">
        <div className="tab-panel active" role="tabpanel" id={`mmcp-tabpanel-${activeTab}`} aria-labelledby={`mmcp-tab-${activeTab}`}>
          {activeTab === 'tenant' && (
            <TenantTab
              remoteEndpoint={detail.remoteEndpoint}
              operations={detail.operations}
              formOperations={form.operations}
              onOperationToggle={handleOperationToggle}
            />
          )}

          {activeTab === 'templates' && (
            <TemplatesTab
              connectionId={detail.id}
              templates={templates}
              tenantOperations={detail.operations}
              userAccess={userAccess}
              defaultPermissionTemplateId={form.defaultPermissionTemplateId}
            />
          )}

          {activeTab === 'users' && (
            <UsersTab
              connectionId={detail.id}
              templates={templates}
              userAccess={userAccess}
              tenantOperations={detail.operations}
              defaultPermissionTemplateId={form.defaultPermissionTemplateId}
              onDefaultPermissionTemplateIdChange={handleDefaultPermissionTemplateIdChange}
            />
          )}
        </div>
      </div>
      {/* ── End .sparks-main ── */}
      </div>

      {/* Tenant-Wide User Access — confirmation before save. States the CONSEQUENCE, not the
          setting (§14a), in whichever direction the save lands. Cancel aborts the save and
          leaves the page dirty — `form` is untouched, so `dirty` stays true. */}
      {defaultAccessConfirm && (
        <ConfirmDialog
          title={t('mambuMcpDetail.users.defaultAccess.confirmDialog.title')}
          warningBody={form.defaultPermissionTemplateId !== null
            ? t('mambuMcpDetail.users.defaultAccess.confirmDialog.messageOn', {
                template: templates.find(tpl => tpl.id === form.defaultPermissionTemplateId)?.name ?? '',
              })
            : t('mambuMcpDetail.users.defaultAccess.confirmDialog.messageOff', {
                count: userAccess.length,
              })}
          confirmLabel={t('mambuMcpDetail.users.defaultAccess.confirmDialog.confirm')}
          cancelLabel={t('mambuMcpDetail.users.defaultAccess.confirmDialog.cancel')}
          onConfirm={commitSave}
          onCancel={() => { setDefaultAccessConfirm(false); setTabToSwitchAfterSave(null) }}
        />
      )}

      {/* Navigation guard */}
      {navBlockDialog && (
        <UnsavedChangesDialog onConfirm={confirmNavLeave} onCancel={cancelNavLeave} />
      )}

      {/* Tab-switch guard, entity-editor case — fires when an open template /
          Individual User Access editor has unsaved changes; discarding here only
          clears the open editor's own draft. */}
      {entityTabSwitchConfirm && pendingTab && (
        <UnsavedChangesDialog
          onConfirm={() => {
            const next = pendingTab
            markAllEntityEditorsClean()
            setEntityTabSwitchConfirm(false)
            setPendingTab(null)
            commitTab(next)
          }}
          onCancel={() => { setEntityTabSwitchConfirm(false); setPendingTab(null) }}
        />
      )}

      {/* Tab-switch guard, page-level case — fires when a plain field/toggle change
          (not routed through an entity editor) has dirtied the page form. */}
      {tabSwitchDialog && pendingTab && (
        <UnsavedChangesDialog
          onConfirm={() => {
            commitDiscard()
            setTabSwitchDialog(false)
            commitTab(pendingTab)
            setPendingTab(null)
          }}
          onCancel={() => { setTabSwitchDialog(false); setPendingTab(null) }}
        />
      )}

      {toast && <SuccessToast message={toast} />}
    </div>
  )
}
